import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Search, Edit, Trash2, Settings, Pin } from 'lucide-react'
import { api } from '../services/api'
import { useLanguageStore } from '../stores/languageStore'
import Button from '../components/Button'
import Input from '../components/Input'
import FieldEditor from '../components/FieldEditor'

export default function BlockTypes() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [schema, setSchema] = useState({ fields: [] })
  const [isFixed, setIsFixed] = useState(false)
  const [fixedEntityTypes, setFixedEntityTypes] = useState([])
  const [fixedIncludeCategories, setFixedIncludeCategories] = useState([])
  const [fixedExcludeCategories, setFixedExcludeCategories] = useState([])
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { currentLanguageCode } = useLanguageStore()

  const { data: blockTypes, isLoading } = useQuery({
    queryKey: ['block-types'],
    queryFn: () => api.get('/block-types').then((res) => res.data),
  })

  const { data: categories = [] } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: async () => {
      const res = await api.get('/categories', { params: { per_page: 100 } })
      return res.data.data || []
    },
  })

  const createBlockTypeMutation = useMutation({
    mutationFn: (blockTypeData) => api.post('/block-types', blockTypeData),
    onSuccess: () => {
      queryClient.invalidateQueries(['block-types'])
      setShowCreateForm(false)
      setSchema({ fields: [] })
      setIsFixed(false)
      setFixedEntityTypes([])
      setFixedIncludeCategories([])
      setFixedExcludeCategories([])
    },
  })

  const deleteBlockTypeMutation = useMutation({
    mutationFn: (id) => api.delete(`/block-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['block-types'])
    },
  })

  const filteredBlockTypes =
    blockTypes?.data?.filter(
      (blockType) =>
        blockType.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blockType.description?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Block Types</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage reusable block type templates.
          </p>
        </div>
        <Button onClick={() => setShowCreateForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Block Type
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search block types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Create Block Type Form */}
      {showCreateForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Create New Block Type
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              const formData = new FormData(e.target)
              const blockTypeData = {
                name: formData.get('name'),
                description: formData.get('description'),
                schema: schema,
                is_fixed: isFixed,
              }

              if (isFixed) {
                blockTypeData.fixed_entity_types =
                  fixedEntityTypes.length > 0 ? fixedEntityTypes : null
                blockTypeData.fixed_include_categories =
                  fixedIncludeCategories.length > 0
                    ? fixedIncludeCategories
                    : null
                blockTypeData.fixed_exclude_categories =
                  fixedExcludeCategories.length > 0
                    ? fixedExcludeCategories
                    : null
              }

              const slug = formData.get('slug')?.trim()
              if (slug) {
                blockTypeData.slug = slug
              }

              if (currentLanguageCode) {
                blockTypeData.locale = currentLanguageCode
              }

              createBlockTypeMutation.mutate(blockTypeData)
            }}
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <Input name="name" placeholder="Block type name" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Slug
                </label>
                <Input name="slug" placeholder="Auto-generated from name" />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to auto-generate from name
                </p>
              </div>
            </div>
            <div className="mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                </label>
                <Input
                  name="description"
                  placeholder="Block type description"
                />
              </div>
            </div>
            <div className="mt-4">
              <FieldEditor schema={schema} onChange={setSchema} />
            </div>
            <div className="mt-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isFixed}
                  onChange={(e) => {
                    setIsFixed(e.target.checked)
                    if (!e.target.checked) {
                      setFixedEntityTypes([])
                      setFixedIncludeCategories([])
                      setFixedExcludeCategories([])
                    }
                  }}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Fixed Block</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Fixed blocks are automatically appended to entities
              </p>
            </div>
            {isFixed && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply to Entity Types
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={fixedEntityTypes.includes('page')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFixedEntityTypes([...fixedEntityTypes, 'page'])
                          } else {
                            setFixedEntityTypes(
                              fixedEntityTypes.filter((t) => t !== 'page')
                            )
                          }
                        }}
                        className="rounded border-gray-300 text-primary-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Pages</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={fixedEntityTypes.includes('post')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFixedEntityTypes([...fixedEntityTypes, 'post'])
                          } else {
                            setFixedEntityTypes(
                              fixedEntityTypes.filter((t) => t !== 'post')
                            )
                          }
                        }}
                        className="rounded border-gray-300 text-primary-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Posts</span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to apply to all entity types
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Include Categories (only)
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setFixedIncludeCategories(
                              categories.map((c) => c.id)
                            )
                          }
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setFixedIncludeCategories([])}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <select
                      multiple
                      value={fixedIncludeCategories.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(
                          e.target.selectedOptions,
                          (option) => Number(option.value)
                        )
                        setFixedIncludeCategories(selected)
                      }}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      size={4}
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name || cat.slug}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Only apply to these categories
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Exclude Categories
                      </label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setFixedExcludeCategories(
                              categories.map((c) => c.id)
                            )
                          }
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => setFixedExcludeCategories([])}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <select
                      multiple
                      value={fixedExcludeCategories.map(String)}
                      onChange={(e) => {
                        const selected = Array.from(
                          e.target.selectedOptions,
                          (option) => Number(option.value)
                        )
                        setFixedExcludeCategories(selected)
                      }}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                      size={4}
                    >
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name || cat.slug}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                      Never apply to these categories
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="mt-4 flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCreateForm(false)
                  setSchema({ fields: [] })
                  setIsFixed(false)
                  setFixedEntityTypes([])
                  setFixedIncludeCategories([])
                  setFixedExcludeCategories([])
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createBlockTypeMutation.isPending}
              >
                {createBlockTypeMutation.isPending
                  ? 'Creating...'
                  : 'Create Block Type'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Block Types Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">Loading block types...</div>
          </div>
        ) : filteredBlockTypes.length === 0 ? (
          <div className="col-span-full text-center py-8">
            <div className="text-gray-500">
              {searchTerm
                ? 'No block types found matching your search.'
                : 'No block types created yet.'}
            </div>
          </div>
        ) : (
          filteredBlockTypes.map((blockType) => (
            <div
              key={blockType.id}
              className="bg-white shadow rounded-lg overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Settings className="h-8 w-8 text-gray-400" />
                    <div className="ml-3">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {blockType.name}
                        </h3>
                        {blockType.is_fixed && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Pin className="h-3 w-3" />
                            Fixed
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {blockType.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        navigate(`/block-types/${blockType.id}/edit`)
                      }
                      className="text-gray-400 hover:text-gray-600"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (
                          confirm(
                            'Are you sure you want to delete this block type?'
                          )
                        ) {
                          deleteBlockTypeMutation.mutate(blockType.id)
                        }
                      }}
                      className="text-red-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="text-xs text-gray-500">
                    Created:{' '}
                    {new Date(blockType.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
